import { Component, OnInit } from '@angular/core';
import { ReportesService } from 'src/app/services/reportes/reportes.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-reporte-cambio-bancarios',
  templateUrl: './reporte-cambio-bancarios.component.html',
  styleUrls: ['./reporte-cambio-bancarios.component.css']
})
export class ReporteCambioBancariosComponent implements OnInit {

  cols = [
    { field: 'nombre_cliente', header: 'Cliente' },
    { field: 'usuario_solicitante', header: 'Solicitante' },
    { field: 'banco', header: 'Banco Actual' },
    { field: 'cuenta_bancaria', header: 'Cuenta Actual' },
    { field: 'cuenta_clabe', header: 'CLABE Actual' },
    { field: 'motivo_cambio', header: 'Motivo' },
    { field: 'fecha_solicitud', header: 'Fecha Solicitud' },
    { field: 'tipo_contribuyente', header: 'Tipo' },
  ];

  solicitudes: any[] = [];
  loading = false;

  // Ordenamiento
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Paginación
  currentPage = 1;
  itemsPerPage = 8;
  searchTerm = '';
  filaSeleccionada: number | null = null;
  pages: (string | number)[] = [];

  constructor(private reportesService: ReportesService) {}

  ngOnInit(): void {
    this.consultarSolicitudes();
  }

  consultarSolicitudes() {
    this.loading = true;
    this.solicitudes = [];
    this.reportesService.getSolicitudesCambioBancarios().subscribe(
      (resp: any[]) => {
        this.solicitudes = resp.map(item => ({
          ...item,
          fecha_solicitud_fmt: this.formatFecha(item.fecha_solicitud),
        }));
        this.loading = false;
        this.pages = this.generatePages();
      },
      (error) => {
        console.error('Error al consultar solicitudes de cambio bancario:', error);
        this.loading = false;
      }
    );
  }

  // Ordenamiento
  sortData(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  private compare(a: any, b: any, column: string, direction: 'asc' | 'desc'): number {
    if (!column) return 0;
    const valueA = a[column] || '';
    const valueB = b[column] || '';
    let comparison = 0;
    if (valueA > valueB) {
      comparison = 1;
    } else if (valueA < valueB) {
      comparison = -1;
    }
    return direction === 'asc' ? comparison : -comparison;
  }

  // Filtrado global
  get filteredData(): any[] {
    const filtered = this.solicitudes.filter(item =>
      (item.nombre_cliente || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.usuario_solicitante || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.banco || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.cuenta_bancaria || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.cuenta_clabe || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.motivo_cambio || '').toLowerCase().includes(this.searchTerm.toLowerCase())
    );
    return filtered.sort((a, b) => this.compare(a, b, this.sortColumn, this.sortDirection));
  }

  get dataToShow(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredData.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // Paginación
  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.itemsPerPage) || 1;
  }

  generatePages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 2;
    const pages: (number | string)[] = [];

    const add = (p: number | string) => {
      if (!pages.includes(p)) pages.push(p);
    };

    add(1);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) add(i);
    }
    add(total);

    const finalPages: (number | string)[] = [];
    let prev: number | string | null = null;

    pages
      .sort((a, b) => Number(a) - Number(b))
      .forEach((p) => {
        if (prev !== null && Number(p) - Number(prev) > 1) {
          finalPages.push('...');
        }
        finalPages.push(p);
        prev = p;
      });

    return finalPages;
  }

  goToPage(page: number | string): void {
    const p = Number(page);
    if (p >= 1 && p <= this.totalPages) {
      this.currentPage = p;
      this.pages = this.generatePages();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pages = this.generatePages();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pages = this.generatePages();
    }
  }

  pgone() {
    this.currentPage = 1;
    this.filaSeleccionada = -1;
  }

  seleccionarFila(index: number) {
    this.filaSeleccionada = index + (this.currentPage - 1) * this.itemsPerPage;
  }

  // Excel export
  exportExcel() {
    const dataToExport = this.filteredData.map(item => {
      const row: any = {};
      row['Cliente'] = item.nombre_cliente || '';
      row['Solicitante'] = item.usuario_solicitante || '';
      row['Banco Actual'] = item.banco || '';
      row['Cuenta Actual'] = item.cuenta_bancaria || '';
      row['CLABE Actual'] = item.cuenta_clabe || '';
      row['Motivo'] = item.motivo_cambio || '';
      row['Fecha Solicitud'] = item.fecha_solicitud_fmt || '';
      row['Tipo Contribuyente'] = item.tipo_contribuyente || '';
      row['Banco Solicitado'] = item.cambios_solicitados?.bank || '';
      row['CLABE Solicitada'] = item.cambios_solicitados?.clabe || '';
      row['Cuenta Solicitada'] = item.cambios_solicitados?.account_number || '';
      return row;
    });
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cambios Bancarios');
    XLSX.writeFile(wb, 'reporte_cambios_bancarios.xlsx');
  }

  // Utilidades
  formatFecha(fecha: string): string {
    if (!fecha) return '';
    const date = new Date(fecha);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  getDisplayValue(item: any, field: string): string {
    if (field === 'fecha_solicitud') {
      return item.fecha_solicitud_fmt || '';
    }
    return item[field] || '';
  }
}
